import { FC, useMemo, useState } from 'react';
import { Box, Button, ListItem, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import Img from '@barracuda/shared/components/Primitive/Img';
import {
  navbarMenuItemButton,
  navbarMenuItemContent,
  navbarMenuItemElement,
  navbarMenuItemTitle,
  navbarMenuItemTitleTextBox,
  navbarMenuSubItemTypography,
  navbarMenuArrowIcon
} from './NavbarMenuItem.styles';
import ArrowUp from '@/assets/images/menu__arrow-up.svg?react';
import ArrowDown from '@/assets/images/menu__arrow-down.svg?react';
import { useLocation } from 'react-router-dom';

const V2_PAGE_PATHS = ['/login', '/join', '/reset-password'];

interface NavbarMenuProps {
  title: string;
  items: NavbarMenuItem[];
}

export interface NavbarMenuItem {
  icon: string | JSX.Element;
  title: string;
  link: string;
  target?: string;
}

export const NavbarMenuItem: FC<NavbarMenuProps> = ({ title, items }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useLocalizedNavigate();
  const { pathname } = useLocation();
  const isV2Page = V2_PAGE_PATHS.some((path) => pathname.includes(path));
  const open = Boolean(anchorEl);
  const ArrowIcon = open ? ArrowUp : ArrowDown;
  const menuId = useMemo(
    () =>
      `navbar-menu-${title
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]/g, '')}`,
    [title]
  );
  const menuButtonId = `${menuId}-button`;

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleTitleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleItemClick = (item: NavbarMenuItem) => {
    setAnchorEl(null);

    if (item.target === '_blank') {
      window.open(item.link, '_blank');
    } else {
      navigate(item.link);
    }
  };

  return (
    <ListItem>
      <Button
        id={menuButtonId}
        sx={navbarMenuItemTitle(open, isV2Page)}
        onClick={handleTitleClick}
        aria-expanded={open}
        aria-haspopup='menu'
        aria-controls={open ? menuId : undefined}
      >
        <Box sx={navbarMenuItemButton}>
          <Box>{title}</Box>
          <Box component='span' aria-hidden sx={navbarMenuArrowIcon(isV2Page)}>
            <ArrowIcon aria-hidden />
          </Box>
        </Box>
      </Button>
      <Menu
        sx={navbarMenuItemContent}
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ 'aria-labelledby': menuButtonId }}
      >
        {items.map((item, index) => (
          <MenuItem
            key={`menu-item-${index}`}
            sx={navbarMenuItemElement}
            onClick={() => handleItemClick(item)}
          >
            <ListItemIcon>
              {typeof item.icon === 'string' ? (
                item.icon ? (
                  <Img src={item.icon} alt={item.title} />
                ) : null
              ) : (
                <Box component='span' aria-hidden sx={{ display: 'flex' }}>
                  {item.icon}
                </Box>
              )}
            </ListItemIcon>

            <ListItemText primaryTypographyProps={navbarMenuSubItemTypography}>
              <Box sx={navbarMenuItemTitleTextBox}>{item.title}</Box>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </ListItem>
  );
};
